import React, {useCallback, useEffect, useRef, useState} from 'react';
import { strings } from 'src/localization';
import css from './UpdateMileage.module.scss';
import { Checkbox, IView, TextField } from '@framework-controls/core';
import { ApptVeh, ascCtaInteractionInput, ascFormEngagementInput, ascPageviewInput, ascElementConfigurationInput } from 'src/models';
import { ascCtaInteraction, ascFormEngagement, ascPageview, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import { ComponentMapIds } from 'src/components/map';

export interface IProps {
    selectedVehicle: ApptVeh,
    prevMileage: string,
    MCReturned: boolean;
    MCError: boolean;
    waitOnMC: boolean;
    TMNAMileage: number;
    toyotaMCLaunched: string;
    lexusMCLaunched: string;
    locale: string;
    mainPaneViewOrder: IView[];
    countryCode: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    idkMileage: string;
    updateVehicleMileage: (mileage: number) => void,
    getMaintContracts: () => void;
    setWaitOnMC: (value: boolean) => void;
    setIdkMileage: (value: string) => void;
}

const UpdateMileage = (props: IProps) => {
    const prevMileage = props.prevMileage.toString();
    const [mileage, setMileage] = useState(0);
    const [unknownBox, setUnknownBox] = useState('');
    const [mileageError, setMileageError] = useState(false);
    const onLoad = useRef(true);

    useEffect(() => {
        if (onLoad.current) {
            props.updateVehicleMileage(props.TMNAMileage >= parseInt(prevMileage) ? props.TMNAMileage : 0);
            onLoad.current = false;
        }
    });

    useEffect(() => {
        const currentPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1];
        if (currentPane.viewId === ComponentMapIds.AddVehicle){
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            const vehicleInput = {
                id: '',
                vin: props.selectedVehicle.vin,
                makeCode: props.selectedVehicle.makeCode,
                make: props.selectedVehicle.make,
                model: props.selectedVehicle.model,
                imagePath: '',
                year: props.selectedVehicle.vehYr,
                color: '',
                style: '',
                prevMileage: 0,
                prevRoDate: '',
                nextAppt: '',
                lastAdv: '',
                lastAdvName: '',
                lastAdvImage: '',
                lastTrans: '',
                lastTransLit: '',
                nameId: '',
            };
            ascPageview(ascPageViewInp, [vehicleInput]);
        }
    }, [props.mainPaneViewOrder, props.selectedVehicle.vin, props.selectedVehicle.makeCode, props.selectedVehicle.make,
        props.selectedVehicle.model, props.selectedVehicle.vehYr]);

    useEffect(() => {
        const maintMake = props.selectedVehicle.make.toLowerCase().split(' ')[0];
        // const maintContractView = (props.selectedVehicle && props.locale === 'en-US' &&
        const maintContractView = (props.selectedVehicle && props.countryCode === 'US' &&
            (((props.lexusMCLaunched || props.lexusTDMCLaunched) && maintMake === 'lexus') ||
            ((props.toyotaMCLaunched || props.toyotaTDMCLaunched) && maintMake === 'toyota')));

        const currentPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1];
        if (maintContractView && !props.MCReturned && !props.MCError && !props.waitOnMC &&
                currentPane.viewId === ComponentMapIds.AddVehicle) {
            props.getMaintContracts();
            props.setWaitOnMC(true);
        }
    }, [props.getMaintContracts, props.setWaitOnMC, props.selectedVehicle, props.countryCode, props.lexusMCLaunched,
        props.toyotaMCLaunched, props.MCReturned, props.MCError, props.waitOnMC, props.mainPaneViewOrder,
        props.lexusTDMCLaunched, props.toyotaTDMCLaunched]);

    useEffect(() => {
        if (props.waitOnMC && (props.MCReturned || props.MCError)) {
            props.setWaitOnMC(false);
            if (props.TMNAMileage && props.TMNAMileage >= parseInt(prevMileage)) {
                setMileage(props.TMNAMileage);
                props.updateVehicleMileage(props.TMNAMileage);
            }
        }
    }, [props.MCReturned,
        props.MCError,
        props.setWaitOnMC,
        props.waitOnMC,
        props.TMNAMileage,
        props.updateVehicleMileage,
        prevMileage]);

    let cbkClassName = 'primaryCkb';
    if (unknownBox === 'Unknown') {
        cbkClassName = 'primaryCkbChecked';
    }

    const mileageChangedHandler = (_id: string, value: string) => {
        if (value.length <= 7) {
            let newMileage = 0;
            if (value === '') {
                newMileage = 0;
            } else {
                const numValue = parseInt(value.replace(/,/g, ''), 10);
                newMileage = isNaN(numValue) ? 0 : numValue;
            }
            setMileage(newMileage);
            props.updateVehicleMileage(newMileage);
        }
    };

    useEffect(() => {
        if (props.selectedVehicle.mileage !== parseInt(prevMileage)){
            setMileage(props.selectedVehicle.mileage);
        }
        if (props.selectedVehicle.mileage === -1 && unknownBox === '') {
            setMileage(0);
        }
    }, [prevMileage, props.selectedVehicle.mileage, unknownBox, props.selectedVehicle]);

    const onCheckedHandler = useCallback((e: any) => {
        if (unknownBox !== 'Unknown') {
            // setUnknownBox(strings.Unknown);
            setUnknownBox('Unknown');
            setMileage(0);
            props.updateVehicleMileage(-1);
            props.setIdkMileage('Y');
            setMileageError(false);
        } else {
            setUnknownBox('');
            props.updateVehicleMileage(0);
            props.setIdkMileage('N');
        }
        const elementColor = getElementColor(e, 'fill');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.UnknownMileage,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.UnknownMileage,
            elementType: 'item_details',
            elementColor: elementColor,
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [props.updateVehicleMileage, unknownBox, props.setIdkMileage]);

    const blurHandler = useCallback(() => {
        const mileageToCheck = props.TMNAMileage > 0 ? props.TMNAMileage.toString() : prevMileage;
        if (mileage < parseInt(mileageToCheck)) {
            setMileageError(true);
        } else {
            setMileageError(false);
        }
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.EnterEstimatedMileage,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp);
    }, [mileage, prevMileage, props.TMNAMileage]);

    const updatedMileage = props.TMNAMileage > 0 && props.TMNAMileage >= parseInt(props.prevMileage) ?
        props.TMNAMileage : props.prevMileage;
    const formattedMileage = new Intl.NumberFormat().format(parseFloat(updatedMileage.toString().replace(/,/g, ''))).toString();

    return (
        <div>
            <span>
                {`${strings.UpdateMilStart}${formattedMileage}${strings.UpdateMilMid}${strings.UpdateMilEnd}` +
                `${strings.RequiredFields}`}
            </span>
            <div className={css.mileageNumberField}>
                <TextField
                    type='tel'
                    id='mileageText'
                    label={strings.EnterEstimatedMileage}
                    value={unknownBox === 'Unknown' ? '0' : mileage?.toLocaleString('en-US')!}
                    onChange={mileageChangedHandler}
                    disabled={unknownBox === 'Unknown'}
                    onBlur={blurHandler}
                />
            </div>
            {!mileageError ? '' :
                <span className={css.mileageLessError}>
                    {strings.UpdateMilLess}
                </span>
            }
            <div className={css.mileageCheckbox}>
                <Checkbox
                    id='estMilCheckbox'
                    checked={unknownBox === 'Unknown'}
                    size='rg'
                    variant={cbkClassName}
                    label={strings.UnknownMileage}
                    labelPlacement='end'
                    onChange={(e) => onCheckedHandler!(e)}
                />
            </div>
        </div>
    );
};

export default UpdateMileage;